import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WorkbenchField, WorkbenchNotice } from '@ndrc/ui';
import { useDesk } from '../desk/context';
import { callCore } from '../lib/rpc';
import { t } from '../messages';
import { dateLabel, sourceKey, type Source } from './model';
interface Projection {
  sourceVersion: string;
  observedAt: string;
  total: number;
  items: { kind: string; externalId: string; title: string; status: string }[];
}
export function SourcePicker({
  sources,
  disabled,
  onChange,
}: {
  sources: Source[];
  disabled: boolean;
  onChange: (sources: Source[]) => void;
}) {
  const { workspace } = useDesk();
  const [environmentId, setEnvironment] = useState(''),
    [search, setSearch] = useState(''),
    [offset, setOffset] = useState(0);
  const environments = useQuery({
    queryKey: ['core', 'external.list', workspace?.workspaceId],
    enabled: Boolean(workspace) && !disabled,
    queryFn: () =>
      callCore<{ environments: { id: string; label: string }[] }>('external.list', {
        workspaceId: workspace!.workspaceId,
      }),
  });
  const query = useQuery({
    queryKey: ['core', 'external.projection', 'practice', environmentId, search, offset],
    enabled: Boolean(environmentId) && !disabled,
    queryFn: () =>
      callCore<{ projection: Projection | null }>('external.projection', {
        environmentId,
        search,
        offset,
      }),
  });
  const projection = query.data?.projection;
  return (
    <details className="practice-picker">
      <summary>{t('practice.addSources', { count: sources.length })}</summary>
      <WorkbenchField label={t('practice.environment')}>
        <select
          disabled={disabled}
          value={environmentId}
          onChange={(event) => {
            setEnvironment(event.target.value);
            setOffset(0);
          }}
        >
          <option value="">{t('practice.chooseEnvironment')}</option>
          {environments.data?.environments.map((env) => (
            <option key={env.id} value={env.id}>
              {env.label}
            </option>
          ))}
        </select>
      </WorkbenchField>
      {environments.isError && (
        <WorkbenchNotice error>{environments.error.message}</WorkbenchNotice>
      )}
      {environmentId && (
        <>
          <div className="lab-actions">
            <WorkbenchField label={t('practice.search')}>
              <input
                maxLength={200}
                value={search}
                disabled={disabled}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setOffset(0);
                }}
              />
            </WorkbenchField>
            <button
              type="button"
              disabled={disabled || query.isFetching}
              onClick={() => {
                void query.refetch();
              }}
            >
              {t('practice.refresh')}
            </button>
          </div>
          {query.isError && <WorkbenchNotice error>{query.error.message}</WorkbenchNotice>}
          {query.isFetching && <p role="status">{t('practice.loading')}</p>}
          {projection ? (
            <>
              <p>{t('practice.observed', { date: dateLabel(projection.observedAt) })}</p>
              <ul className="practice-sources">
                {projection.items.map((item) => {
                  const source = {
                    environmentId,
                    kind: item.kind,
                    externalId: item.externalId,
                    sourceVersion: projection.sourceVersion,
                  };
                  const exists = sources.some((s) => sourceKey(s) === sourceKey(source));
                  return (
                    <li key={sourceKey(source)}>
                      <span>{item.title}</span>
                      <button
                        type="button"
                        disabled={disabled || query.isFetching || (!exists && sources.length >= 20)}
                        onClick={() =>
                          onChange([
                            ...sources.filter((s) => sourceKey(s) !== sourceKey(source)),
                            source,
                          ])
                        }
                      >
                        {t(exists ? 'practice.updateSource' : 'practice.selectSource')}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="lab-actions">
                <button
                  type="button"
                  disabled={disabled || offset === 0 || query.isFetching}
                  onClick={() => setOffset(Math.max(0, offset - 20))}
                >
                  {t('practice.previous')}
                </button>
                <button
                  type="button"
                  disabled={
                    disabled ||
                    offset + projection.items.length >= projection.total ||
                    query.isFetching
                  }
                  onClick={() => setOffset(offset + projection.items.length)}
                >
                  {t('practice.next')}
                </button>
              </div>
            </>
          ) : (
            !query.isFetching && <p>{t('practice.noProjection')}</p>
          )}
        </>
      )}
    </details>
  );
}
